/**
 * PreferencesCard.js — RC 1.43.B Unified Preference Centre.
 *
 * Settings → Preferences. Single source of truth for every
 * candidate preference NDORFlow uses today:
 *
 *   • preferred_regions        (chip)
 *   • location_strictness      (select)
 *   • open_to_relocation       (select)
 *   • relocation_regions       (chip, gated)
 *   • include_cover_letter     (switch)
 *   • discoverable_to_employers (switch)
 *
 *   RC 1.43.A new fields (now editable):
 *   • preferred_titles         (chip, free text, cap 10)
 *   • industries               (chip, free text, cap 10)
 *   • excluded_keywords        (chip, free text, cap 20)
 *   • employment_types         (chip-toggle, fixed vocab)
 *   • seniority_targets        (chip-toggle, fixed vocab)
 *   • remote_preferences       (select)
 *   • salary_floor / ceiling   (number inputs)
 *   • salary_currency / period (selects)
 *
 * Reads / writes exclusively through `/api/profile/preferences`.
 * No local persistence. Saves are atomic and re-read the server
 * response so client state can never drift.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertTriangle, Briefcase, DollarSign, Globe, Loader2,
  MapPin, Save, Tag, X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

import { apiFetch } from '@/lib/api';
import {
  ARRAY_FIELD_CAP, EMPLOYMENT_TYPE_OPTIONS, EXCLUDED_KEYWORDS_CAP,
  PREFERENCE_DEFAULTS, REMOTE_PREF_OPTIONS,
  SALARY_CURRENCY_OPTIONS, SALARY_PERIOD_OPTIONS, SENIORITY_OPTIONS,
  diffPreferences, parseSalaryBound,
} from '@/components/preferencesHelpers';

const STRICTNESS_OPTIONS = [
  { value: 'strict',   label: 'Strict — only show matching locations' },
  { value: 'balanced', label: 'Balanced — flex with relocation openness' },
  { value: 'open',     label: 'Open — barely penalise non-matches' },
];
const RELOCATION_OPTIONS = [
  { value: 'yes',         label: 'Yes — I would relocate' },
  { value: 'conditional', label: 'Conditional — depends on role/package' },
  { value: 'no',          label: 'No — location is fixed' },
];

// ─── Free-text chip input ─────────────────────────────────────────

function ChipInput({ value, onChange, placeholder, testId, cap }) {
  const [draft, setDraft] = useState('');
  const atCap = cap > 0 && (value || []).length >= cap;
  const commit = () => {
    if (atCap) return;
    const s = draft.trim();
    if (!s) return;
    if (value.some((v) => v.toLowerCase() === s.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, s]);
    setDraft('');
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
        {value.map((v, i) => (
          <Badge
            key={`${v}-${i}`}
            variant="outline"
            className="bg-ndor-surface border-ndor-edge text-xs gap-1 pl-2.5"
          >
            {v}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="text-ndor-ink-muted hover:text-ndor-error ml-0.5"
              aria-label={`Remove ${v}`}
              data-testid={`${testId}-chip-remove-${i}`}
            >
              <XIcon className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {value.length === 0 && (
          <span className="text-xs text-ndor-ink-muted italic">None set</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          }}
          placeholder={placeholder}
          className="h-9 text-sm"
          disabled={atCap}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button" variant="outline" size="sm"
          onClick={commit} disabled={!draft.trim() || atCap}
          data-testid={`${testId}-add`}
        >
          Add
        </Button>
      </div>
      {cap > 0 && (
        <p className="text-[11px] text-ndor-ink-muted">
          {(value || []).length} / {cap}
          {atCap ? ' — limit reached' : ''}
        </p>
      )}
    </div>
  );
}

// ─── Toggleable enum-chip group (employment / seniority) ──────────

function EnumChipGroup({ value, onChange, options, testId }) {
  const set = new Set(value || []);
  const toggle = (v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-group`}>
      {options.map((o) => {
        const active = set.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={[
              'text-xs px-2.5 py-1 rounded-full border transition-colors',
              active
                ? 'bg-ndor-ink text-ndor-surface border-ndor-ink'
                : 'bg-ndor-surface text-ndor-ink border-ndor-edge hover:bg-ndor-surface-alt',
            ].join(' ')}
            data-testid={`${testId}-${o.value}`}
            aria-pressed={active}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-start gap-2 pt-1">
      <Icon className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
      <div>
        <h3 className="text-sm font-medium text-ndor-ink leading-tight">{title}</h3>
        {hint && <p className="text-[11px] text-ndor-ink-muted mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

// ─── Main editor ──────────────────────────────────────────────────

export default function PreferencesCard() {
  const [server, setServer]   = useState(null);
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [unmigrated, setUnmigrated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/profile/preferences');
      const merged = { ...PREFERENCE_DEFAULTS, ...(r.preferences || {}) };
      setServer(merged);
      setForm({ ...merged });
      setUnmigrated(Boolean(r.unmigrated));
    } catch (err) {
      toast.error(err?.message || 'Failed to load preferences.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = useMemo(
    () => (server && form ? diffPreferences(server, form) : {}),
    [server, form],
  );
  const isDirty = Object.keys(patch).length > 0;

  // Salary bounds editor needs to keep raw text as the user types
  // so they can clear / re-type freely. We coerce on save.
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    if (!isDirty || saving) return;
    // Coerce salary bounds before POST.
    const cleaned = { ...patch };
    if ('salary_floor'   in cleaned) cleaned.salary_floor   = parseSalaryBound(cleaned.salary_floor);
    if ('salary_ceiling' in cleaned) cleaned.salary_ceiling = parseSalaryBound(cleaned.salary_ceiling);

    if (
      typeof cleaned.salary_floor === 'number'
      && typeof cleaned.salary_ceiling === 'number'
      && cleaned.salary_floor > cleaned.salary_ceiling
    ) {
      toast.error('Salary minimum cannot exceed maximum.');
      return;
    }

    setSaving(true);
    try {
      const r = await apiFetch('/api/profile/preferences', {
        method: 'POST',
        body: cleaned,
      });
      const merged = { ...PREFERENCE_DEFAULTS, ...(r.preferences || {}) };
      setServer(merged);
      setForm({ ...merged });
      setUnmigrated(Boolean(r.unmigrated));
      toast.success(
        r.unmigrated
          ? 'Saved in session — apply migration 023 to persist.'
          : 'Preferences saved.',
      );
      track('preferences_updated', { unmigrated: !!r.unmigrated });
      window.dispatchEvent(new Event('ndor:profile-refresh'));
      window.dispatchEvent(new Event('ndor:preferences-changed'));
    } catch (err) {
      toast.error(err?.data?.detail || err?.message || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => { if (server) setForm({ ...server }); };

  if (loading || !form) {
    return (
      <Card className="border-ndor-edge shadow-sm" data-testid="preferences-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-medium text-ndor-ink">
            <MapPin className="h-5 w-5 text-ndor-ink-muted" />
            Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-ndor-edge shadow-sm" data-testid="preferences-card" id="preferences-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-medium text-ndor-ink">
          <MapPin className="h-5 w-5 text-ndor-ink-muted" />
          Preferences
        </CardTitle>
        <p className="text-xs text-ndor-ink-muted pt-1">
          These guide scoring and document generation — they never hide jobs.
          The Jobs page and NDOR Analyse Fit read the same preferences.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {unmigrated && (
          <div
            className="flex items-start gap-2 p-2.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs"
            data-testid="preferences-unmigrated-warning"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Preferences storage column is missing. Changes won't persist
              until an admin applies migration{' '}
              <code className="font-mono">023_profile_preferences_json.sql</code>.
            </span>
          </div>
        )}

        {/* ── Roles & Industries ──────────────────────────── */}
        <SectionHeader
          icon={Briefcase}
          title="Roles & industries"
          hint="Helps NDOR weigh strategic alignment when scoring jobs."
        />
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Preferred titles</Label>
            <ChipInput
              value={form.preferred_titles || []}
              onChange={(v) => setField('preferred_titles', v)}
              placeholder="e.g. Director of Engineering"
              testId="preferences-card-preferred-titles"
              cap={ARRAY_FIELD_CAP}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Industries</Label>
            <ChipInput
              value={form.industries || []}
              onChange={(v) => setField('industries', v)}
              placeholder="e.g. Energy, Fintech, Healthtech"
              testId="preferences-card-industries"
              cap={ARRAY_FIELD_CAP}
            />
            <p className="text-[11px] text-ndor-ink-muted italic">
              Free text for now — a curated industry taxonomy is on the roadmap.
            </p>
          </div>
        </div>

        {/* ── Employment & Seniority ─────────────────────── */}
        <SectionHeader
          icon={Tag}
          title="Employment & seniority"
          hint="Tap chips to toggle. Multiple selections allowed."
        />
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Employment types</Label>
            <EnumChipGroup
              value={form.employment_types || []}
              onChange={(v) => setField('employment_types', v)}
              options={EMPLOYMENT_TYPE_OPTIONS}
              testId="preferences-card-employment-types"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Seniority targets</Label>
            <EnumChipGroup
              value={form.seniority_targets || []}
              onChange={(v) => setField('seniority_targets', v)}
              options={SENIORITY_OPTIONS}
              testId="preferences-card-seniority-targets"
            />
          </div>
        </div>

        {/* ── Location & Remote ──────────────────────────── */}
        <SectionHeader
          icon={Globe}
          title="Location & remote"
          hint="Used for the Jobs filter and the location sub-score."
        />
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Preferred regions</Label>
            <ChipInput
              value={form.preferred_regions || []}
              onChange={(v) => setField('preferred_regions', v)}
              placeholder="e.g. London, Kuala Lumpur"
              testId="preferences-card-preferred-regions"
              cap={ARRAY_FIELD_CAP}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Remote preference</Label>
              <Select
                value={form.remote_preferences}
                onValueChange={(v) => setField('remote_preferences', v)}
              >
                <SelectTrigger className="h-10" data-testid="preferences-card-remote-pref">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMOTE_PREF_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Location strictness</Label>
              <Select
                value={form.location_strictness}
                onValueChange={(v) => setField('location_strictness', v)}
              >
                <SelectTrigger className="h-10" data-testid="preferences-card-strictness">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRICTNESS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Open to relocation</Label>
            <Select
              value={form.open_to_relocation}
              onValueChange={(v) => setField('open_to_relocation', v)}
            >
              <SelectTrigger className="h-10" data-testid="preferences-card-relocation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RELOCATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.open_to_relocation !== 'no' && (
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Relocation regions (optional)</Label>
              <ChipInput
                value={form.relocation_regions || []}
                onChange={(v) => setField('relocation_regions', v)}
                placeholder="e.g. Dublin, Singapore"
                testId="preferences-card-relocation-regions"
                cap={ARRAY_FIELD_CAP}
              />
            </div>
          )}
        </div>

        {/* ── Salary ─────────────────────────────────────── */}
        <SectionHeader
          icon={DollarSign}
          title="Salary expectations"
          hint="Used by NDOR to score the salary fit. Leave blank to skip."
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Minimum</Label>
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={form.salary_floor ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setField('salary_floor', v === '' ? null : Number(v));
              }}
              placeholder="80000"
              data-testid="preferences-card-salary-floor"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Maximum</Label>
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={form.salary_ceiling ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setField('salary_ceiling', v === '' ? null : Number(v));
              }}
              placeholder="140000"
              data-testid="preferences-card-salary-ceiling"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Currency</Label>
            <Select
              value={form.salary_currency}
              onValueChange={(v) => setField('salary_currency', v)}
            >
              <SelectTrigger className="h-10" data-testid="preferences-card-salary-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SALARY_CURRENCY_OPTIONS.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-ndor-ink">Period</Label>
            <Select
              value={form.salary_period}
              onValueChange={(v) => setField('salary_period', v)}
            >
              <SelectTrigger className="h-10" data-testid="preferences-card-salary-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SALARY_PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── Exclusions ─────────────────────────────────── */}
        <SectionHeader
          icon={XIcon}
          title="Excluded keywords"
          hint="NDOR flags roles whose description matches these as a risk in key_risks."
        />
        <ChipInput
          value={form.excluded_keywords || []}
          onChange={(v) => setField('excluded_keywords', v)}
          placeholder="e.g. recruiter spam, blockchain"
          testId="preferences-card-excluded-keywords"
          cap={EXCLUDED_KEYWORDS_CAP}
        />

        {/* ── Workflow toggles ───────────────────────────── */}
        <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-ndor-edge bg-ndor-surface-alt">
          <div className="space-y-0.5 pr-2">
            <Label className="text-ndor-ink">Include cover letter when generating a pack</Label>
            <p className="text-xs text-ndor-ink-muted">
              Mirrors the Workflow page toggle. Same preference, same source of truth.
            </p>
          </div>
          <Switch
            checked={Boolean(form.include_cover_letter)}
            onCheckedChange={(v) => setField('include_cover_letter', Boolean(v))}
            data-testid="preferences-card-include-cover-toggle"
          />
        </div>

        <div
          className="flex items-start justify-between gap-4 p-3 rounded-md border border-ndor-edge bg-ndor-surface-alt"
          data-testid="preferences-card-discoverability-row"
        >
          <div className="space-y-0.5 pr-2">
            <Label className="text-ndor-ink">Discoverable by employers</Label>
            <p className="text-xs text-ndor-ink-muted">
              Employers can discover your profile for relevant opportunities.
              This is off unless you turn it on.
            </p>
          </div>
          <Switch
            checked={Boolean(form.discoverable_to_employers)}
            onCheckedChange={(v) => setField('discoverable_to_employers', Boolean(v))}
            data-testid="preferences-card-discoverability-toggle"
          />
        </div>

        {/* ── Actions ───────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 pt-1 border-t border-ndor-edge">
          <Button
            variant="ghost" size="sm"
            onClick={onRevert}
            disabled={!isDirty || saving}
            data-testid="preferences-card-revert"
          >
            Revert
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!isDirty || saving}
            data-testid="preferences-card-save"
            className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {saving ? 'Saving…' : 'Save preferences'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
